import { Injectable } from '@angular/core';
import {
  ChatbotMessagePair,
  ChatbotMessagePairCreate,
  ChatbotMessagePairCreateTeam, MessagePairProfile
} from '../state/chatbot-message-pair';
import * as moment from 'moment';
import {ChatbotChat} from '../state/chatbot-chat';
import {ChatbotErrorService} from './chatbot-error.service';
import {ChatbotMessagePairService} from '../api-services/chatbot-message-pair.service';
import {CurrentUser} from '../../user/current-user.service';
import {ChatbotChatService} from '../api-services/chatbot-chat.service';
import {TranslatorService} from '../../translation/translator.service';
import {ChatbotChatUserForTeams} from '../state/chatbot-chat-user';
import {ChatbotProfileProviderService} from '../services/chatbot-profile-provider.service';

@Injectable({
  providedIn: 'root'
})
export class ChatbotSendMessageService {

  constructor(private chatbotErrorService: ChatbotErrorService,
              private chatbotMessagePairService: ChatbotMessagePairService,
              private chatbotChatService: ChatbotChatService,
              private translatorService: TranslatorService,
              private currentUser: CurrentUser,
              private chatbotProfileProviderService: ChatbotProfileProviderService,
  ) {
  }

  async sendMessage(message: string, currentChat: ChatbotChat): Promise<void> {
    currentChat.sending = true;

    const messageForBackend = this.chatbotProfileProviderService.replaceNamesWithAcronyms(message);

    const newChatbotMessage = new ChatbotMessagePairCreate(currentChat.chat_id, messageForBackend,
      moment().locale(this.currentUser.locale));

    const userMsgToDisplay = new ChatbotMessagePair(currentChat.chat_id, message, moment().locale(this.currentUser.locale), -1,
      this.__('answerComing'), undefined, undefined, []);
    currentChat.messages.push(userMsgToDisplay);

    let too_long = false;
    return new Promise((resolve, reject) => {
      this.chatbotMessagePairService.addMessagePairStreaming(newChatbotMessage).subscribe({
        next: chunk => {
          this.chatbotErrorService.setErrorOccurred(false);
          let data: {choices?: {delta?: {content?: string}; finish_reason?: string}[]}[] | null = null;
          try {
            if (chunk.partialText) {
              const jsonString = '[' + chunk.partialText
                .split('\n')
                .map((line: string) => line.replace(/^data:\s*/, '').trim())
                .filter((line: string) => line.length > 0)
                .join(',') + ']';
              data = JSON.parse(jsonString) as {choices?: {delta?: {content?: string}; finish_reason?: string}[]}[];
            }
          }
          catch (error) {}

          if (data) {
            const lastMessageIndex = currentChat.messages.length - 1;
            const currentMessage = currentChat.messages[lastMessageIndex];

            // Reconstruct full answer from ALL deltas in partialText
            let answer = '';
            for (const responseChunk of data) {
              const choice = responseChunk?.choices?.[0];
              if (choice?.finish_reason) {
                too_long = (choice.finish_reason === 'length');
              }
              else if (choice?.delta?.content) {
                answer += choice.delta.content;
              }
            }

            const displayAnswer = this.chatbotProfileProviderService.replaceKeycloakIdsWithNames(answer);

            currentChat.messages[lastMessageIndex] = new ChatbotMessagePair(
              currentMessage.chat_id,
              currentMessage.question,
              currentMessage.timestamp,
              currentMessage.question_id,
              displayAnswer,
              currentMessage.answer_rating,
              currentMessage.comment,
              currentMessage.relevant_documents
            );
          }
        },
        error: error => {
          this.chatbotErrorService.setErrorOccurred(true);
          this.chatbotErrorService.setErrorMessage(this.__('errorMessage'));
          reject(error);

          setTimeout(() => {
            this.chatbotErrorService.setErrorOccurred(false);
          }, 10000);

          currentChat.sending = false;
          resolve();
        },
        complete: async () => {
          if (too_long) {
            this.chatbotErrorService.setErrorOccurred(true);
            this.chatbotErrorService.setErrorMessage(this.__('errorMessageLength'));
          }
          const chatbotChat: ChatbotChat = await this.chatbotChatService.getChatByID(currentChat.chat_id).toPromise();
          chatbotChat.messages.sort((a, b) => a.question_id - b.question_id);

          const processedMessages = chatbotChat.messages.map(message => new ChatbotMessagePair(
            message.chat_id,
            message.question,
            message.timestamp,
            message.question_id,
            this.chatbotProfileProviderService.replaceKeycloakIdsWithNames(message.answer),
            message.answer_rating,
            message.comment,
            message.relevant_documents
          ));

          currentChat.messages.splice(0, currentChat.messages.length, ...processedMessages);

          if (currentChat.messages.length === 1) {
            const chatbotChatTit: ChatbotChat = await this.chatbotChatService.generateChatTitleByID(currentChat.chat_id).toPromise();
            currentChat.title = this.chatbotProfileProviderService.replaceKeycloakIdsWithNames(chatbotChatTit.title);
          }
          currentChat.sending = false;
          resolve();
        }
      });
    });
  }

  async sendMessageTeam(message: string, currentChat: ChatbotChat, profiles: MessagePairProfile[]): Promise<void> {
    currentChat.sending = true;

    const messageForBackend = this.chatbotProfileProviderService.replaceNamesWithAcronyms(message);

    const newChatbotMessageTeam = new ChatbotMessagePairCreateTeam(currentChat.chat_id, messageForBackend, profiles,
      moment().locale(this.currentUser.locale));

    const userMsgToDisplay = new ChatbotMessagePair(currentChat.chat_id, message, moment().locale(this.currentUser.locale), -1,
      this.__('answerComing'), undefined, undefined, []);
    currentChat.messages.push(userMsgToDisplay);

    let too_long = false;
    return new Promise((resolve, reject) => {
      this.chatbotMessagePairService.addMessagePairStreamingTeam(newChatbotMessageTeam).subscribe({
        next: chunk => {
          this.chatbotErrorService.setErrorOccurred(false);
          let data: {choices?: {delta?: {content?: string}; finish_reason?: string}[]}[] | null = null;
          try {
            if (chunk.partialText) {
              const jsonString = '[' + chunk.partialText
                .split('\n')
                .map((line: string) => line.replace(/^data:\s*/, '').trim())
                .filter((line: string) => line.length > 0)
                .join(',') + ']';
              data = JSON.parse(jsonString) as {choices?: {delta?: {content?: string}; finish_reason?: string}[]}[];
            }
          }
          catch (error) {}

          if (data) {
            const lastMessageIndex = currentChat.messages.length - 1;
            const currentMessage = currentChat.messages[lastMessageIndex];

            // Reconstruct full answer from ALL deltas in partialText
            let answer = '';
            for (const responseChunk of data) {
              const choice = responseChunk?.choices?.[0];
              if (choice?.finish_reason) {
                too_long = (choice.finish_reason === 'length');
              }
              else if (choice?.delta?.content) {
                answer += choice.delta.content;
              }
            }

            const displayAnswer = this.chatbotProfileProviderService.replaceKeycloakIdsWithNames(answer);

            currentChat.messages[lastMessageIndex] = new ChatbotMessagePair(
              currentMessage.chat_id,
              currentMessage.question,
              currentMessage.timestamp,
              currentMessage.question_id,
              displayAnswer,
              currentMessage.answer_rating,
              currentMessage.comment,
              currentMessage.relevant_documents
            );
          }
        },
        error: error => {
          this.chatbotErrorService.setErrorOccurred(true);
          this.chatbotErrorService.setErrorMessage(this.__('errorMessage'));
          reject(error);

          setTimeout(() => {
            this.chatbotErrorService.setErrorOccurred(false);
          }, 10000);

          currentChat.sending = false;
          resolve();
        },
        complete: async () => {
          if (too_long) {
            this.chatbotErrorService.setErrorOccurred(true);
            this.chatbotErrorService.setErrorMessage(this.__('errorMessageLength'));
          }
          const chatbotChat: ChatbotChat = await this.chatbotChatService.getChatByID(currentChat.chat_id).toPromise();
          chatbotChat.messages.sort((a, b) => a.question_id - b.question_id);

          const processedMessages = chatbotChat.messages.map(message => new ChatbotMessagePair(
            message.chat_id,
            message.question,
            message.timestamp,
            message.question_id,
            this.chatbotProfileProviderService.replaceKeycloakIdsWithNames(message.answer),
            message.answer_rating,
            message.comment,
            message.relevant_documents
          ));

          currentChat.messages.splice(0, currentChat.messages.length, ...processedMessages);

          if (currentChat.messages.length === 1) {
            const chatbotChatTit: ChatbotChat = await this.chatbotChatService.generateChatTitleByID(currentChat.chat_id).toPromise();
            currentChat.title = this.chatbotProfileProviderService.replaceKeycloakIdsWithNames(chatbotChatTit.title);
          }
          currentChat.sending = false;
          resolve();
        }
      });
    });
  }

  __(key: string): string {
    return this.translatorService.translate('chatbot-send-message.' + key);
  }

}
